import React, {FC} from 'react'
import clsx from 'clsx'
import {useIntl} from 'react-intl'
import {ReactCountryFlag} from 'react-country-flag'
import {KTIcon} from '@/_metronic/helpers'
import {Transaction, Order} from '../../core/_models'

type OperationCellProps = {
  type: string
  trx?: Transaction
  order?: Order
}

export const OperationCell: FC<OperationCellProps> = ({type, trx, order}) => {
  const intl = useIntl()
  return (
    <div className='d-flex align-items-center'>
      <div className='d-flex flex-column text-muted'>
        <div>
          <div className={clsx('badge', {
            'badge-light-primary': type === 'payment',
            'badge-light-info': type === 'order',
            'badge-light-warning': type === 'sponsorship',
            'badge-light-danger': type === 'exceptional_credits',
          })}>
            {intl.formatMessage({id: `P2C.OPERATION.TYPE_${type.toUpperCase()}`})}
          </div>
        </div>
        <div className="ms-2 pt-1">
          {trx ? (
            <>
              <ReactCountryFlag countryCode={trx.country} style={{ fontSize: '1.5em' }} className='me-3' />
              {intl.formatMessage({id: `P2C.PAYMODE_${(trx.type || 'UNKNOW').toUpperCase()}`})}
              <span className="ms-2">
                {intl.formatNumber(trx.amount || 0, {
                  style: 'currency',
                  currency: trx.currency,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </span>
            </>
          ) : order ? (
            <span className="d-flex align-items-center">
              {order.gift && (
                <span className="pe-3" title={order.gift.email}>
                  <KTIcon iconName='gift' className="fs-1 text-primary" />
                </span>
              )}
              {order.product.name}
            </span>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  )
}

type StatusCellProps = {
  type: string
  status: string
}

export const StatusCell: FC<StatusCellProps> = ({type, status}) => {
  const intl = useIntl()
  let prefix

  if (type === 'order') {
    prefix = 'ORDER_STATUS_'
  } else if (type === 'payment') {
    prefix = 'TRX_STATUS_'
  } else {
    prefix = type.toUpperCase() + '_'
  }

  return (
    <div className={clsx('badge', {
      'badge-light-primary': status === 'PENDING',
      'badge-light-danger': ['FAILED', 'CANCELLED'].includes(status),
      'badge-light-success': status === 'SUCCEED',
      'badge-light-warning': status === 'COUNTRY_MISSMATCH'
    })}>
      {intl.formatMessage({id: `P2C.OPERATION.${prefix}${status}`})}
    </div>
  )
}

type CreditProps = {
  credit?: number
  debit?:number
  type: string
  status: string
}

export const CreditCell: FC<CreditProps> = ({credit, debit, type, status}) => {
  const intl = useIntl()

  if ((credit === null || credit === undefined) && type === 'payment' && status === 'SUCCEED') {
    return (
      <span title="Missing credits">
        <KTIcon iconName='information' className='fs-1 text-danger' />
      </span>
    )
  }

  if ((credit === null || credit === undefined) && (debit === null || debit === undefined) || status !== 'SUCCEED') {
    return (<></>)
  }

  return (
    <div className={clsx('badge', {
      'badge-light-success': credit !== null && credit !== undefined,
      'badge-light-danger': debit !== null && debit !== undefined
    })}>
      {credit !== null && credit !== undefined ? (
        <>+ {intl.formatNumber(credit)}</>
      ) : (
        <>- {intl.formatNumber(debit || 0)}</>
      )}
    </div>
  )
}
